import { useEffect, useRef, useState } from 'react'
import { Download, Paperclip, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { t } from '@/shared/lib/i18n'
import type WebInstrumentsDocumentAttachment from '@/shared/types/web/WebInstrumentsDocumentAttachment'
import {
  downloadLotAttachment,
  DOWNLOAD_ABORT_REASON_TIMEOUT,
  DOWNLOAD_ABORT_REASON_UNMOUNT,
  isTrustedDownloadHref,
  resolveAttachmentFilename,
} from './downloadLotAttachment'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

/**
 * Initial hung window for response headers. Rails `#download` buffers the full S3
 * object before sending headers, so TTFB includes that server-side fetch for ≤20MB
 * blobs — keep this longer than the body-read window to avoid false downloadFailed
 * on slow S3 (AC2).
 */
export const DOWNLOAD_TTFB_TIMEOUT_MS = 300_000

/**
 * Body-read hung window after headers are accepted. Restarted on file-response
 * accept so a stalled transfer cannot leave controls disabled forever, while still
 * allowing slow ≤20MB client transfers (AC2).
 */
export const DOWNLOAD_REQUEST_TIMEOUT_MS = 180_000

export type PendingFile = {
  id: string
  file: File
  description: string
}

type LotFormAttachmentsSectionProps = {
  attachmentsList: WebInstrumentsDocumentAttachment[]
  pendingFiles: PendingFile[]
  onPendingFilesChange: (files: PendingFile[]) => void
  onDeleteExisting?: (attachmentId: number) => void
  deletedAttachmentIds: number[]
  permittedToCreateAttachmentDocuments: boolean
  permittedToDeleteAttachmentDocuments: boolean
}

type InFlightDownload = {
  /** Omitted for blank/untrusted immediate-failure locks (no live fetch to abort). */
  abortController?: AbortController
  /** `undefined` until armed — never use `0` (avoids `clearTimeout(0)` clearing an unrelated timer). */
  timeoutId: number | undefined
  timedOut: boolean
}

const fileExtensionBadge = (filename: string) => filename.split('.').pop()?.toUpperCase() ?? '—'

export const LotFormAttachmentsSection = ({
  attachmentsList,
  pendingFiles,
  onPendingFilesChange,
  onDeleteExisting,
  deletedAttachmentIds,
  permittedToCreateAttachmentDocuments,
  permittedToDeleteAttachmentDocuments,
}: LotFormAttachmentsSectionProps) => {
  const { formatDateTime } = useSiteDateTime()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inFlightDownloadsRef = useRef(new Map<number, InFlightDownload>())
  const isMountedRef = useRef(true)
  const [downloadingAttachmentIds, setDownloadingAttachmentIds] = useState<number[]>([])

  useEffect(() => {
    isMountedRef.current = true
    const inFlightDownloads = inFlightDownloadsRef.current

    return () => {
      isMountedRef.current = false
      for (const inFlightDownload of inFlightDownloads.values()) {
        if (inFlightDownload.timeoutId !== undefined) {
          window.clearTimeout(inFlightDownload.timeoutId)
        }
        inFlightDownload.abortController?.abort(DOWNLOAD_ABORT_REASON_UNMOUNT)
      }
      inFlightDownloads.clear()
    }
  }, [])

  const visibleAttachments = attachmentsList.filter((row) => !deletedAttachmentIds.includes(row.id))

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files?.length) return

    const newPending: PendingFile[] = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file,
      description: '',
    }))
    onPendingFilesChange([...pendingFiles, ...newPending])

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const updatePendingDescription = (id: string, description: string) => {
    onPendingFilesChange(pendingFiles.map((row) => (row.id === id ? { ...row, description } : row)))
  }

  const removePending = (id: string) => {
    onPendingFilesChange(pendingFiles.filter((row) => row.id !== id))
  }

  const handleDownload = async (row: WebInstrumentsDocumentAttachment) => {
    if (inFlightDownloadsRef.current.has(row.id)) return

    const markRowDownloading = (): void => {
      setDownloadingAttachmentIds((currentIds) =>
        currentIds.includes(row.id) ? currentIds : [...currentIds, row.id],
      )
    }
    const clearRowDownloading = (): void => {
      if (!isMountedRef.current) return
      setDownloadingAttachmentIds((currentIds) =>
        currentIds.filter((attachmentId) => attachmentId !== row.id),
      )
    }

    // Blank / untrusted href fails immediately in the helper — skip AbortController / timer.
    // Still lock the row so rapid double-clicks cannot spam toasts.
    if (!row.downloadHref?.trim() || !isTrustedDownloadHref(row.downloadHref)) {
      const immediateFailureLock: InFlightDownload = {
        timeoutId: undefined,
        timedOut: false,
      }
      inFlightDownloadsRef.current.set(row.id, immediateFailureLock)
      markRowDownloading()
      try {
        await downloadLotAttachment(row.downloadHref, row.attachmentName, undefined, {
          isActive: () => isMountedRef.current && inFlightDownloadsRef.current.has(row.id),
        })
      } finally {
        inFlightDownloadsRef.current.delete(row.id)
        clearRowDownloading()
      }
      return
    }

    const abortController = new AbortController()
    const inFlightDownload: InFlightDownload = {
      abortController,
      timeoutId: undefined,
      timedOut: false,
    }
    const armDownloadTimeout = (timeoutMs: number): void => {
      if (!isMountedRef.current) return
      if (!inFlightDownloadsRef.current.has(row.id)) return
      // Never re-arm after abort (unmount or prior timeout) — avoids leaked timers.
      if (abortController.signal.aborted) return
      if (inFlightDownload.timeoutId !== undefined) {
        window.clearTimeout(inFlightDownload.timeoutId)
      }
      const nextTimeoutId = window.setTimeout(() => {
        if (!isMountedRef.current) return
        if (!inFlightDownloadsRef.current.has(row.id)) return
        inFlightDownload.timedOut = true
        abortController.abort(DOWNLOAD_ABORT_REASON_TIMEOUT)
      }, timeoutMs)
      // Unmount may clear the map between schedule and assign — drop the orphan timer.
      if (
        !isMountedRef.current ||
        !inFlightDownloadsRef.current.has(row.id) ||
        abortController.signal.aborted
      ) {
        window.clearTimeout(nextTimeoutId)
        return
      }
      inFlightDownload.timeoutId = nextTimeoutId
    }

    inFlightDownloadsRef.current.set(row.id, inFlightDownload)
    // First window: hung TTFB / headers (includes Rails full-buffer S3). Body phase below.
    armDownloadTimeout(DOWNLOAD_TTFB_TIMEOUT_MS)
    markRowDownloading()

    try {
      await downloadLotAttachment(row.downloadHref, row.attachmentName, abortController.signal, {
        isActive: () => isMountedRef.current && inFlightDownloadsRef.current.has(row.id),
        didTimeout: () => inFlightDownload.timedOut,
        onFileResponseAccepted: () => {
          // Give the ≤20MB body its own hung-transfer window (AC2); do not clear forever.
          armDownloadTimeout(DOWNLOAD_REQUEST_TIMEOUT_MS)
        },
      })
    } finally {
      if (inFlightDownload.timeoutId !== undefined) {
        window.clearTimeout(inFlightDownload.timeoutId)
      }
      inFlightDownloadsRef.current.delete(row.id)
      clearRowDownloading()
    }
  }

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <Paperclip className="h-4 w-4 text-muted-foreground" />
        {t('instruments.lotsForm.attachments.sectionTitle')}
        {(visibleAttachments.length > 0 || pendingFiles.length > 0) && (
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {visibleAttachments.length + pendingFiles.length}
          </span>
        )}
      </h2>

      {visibleAttachments.length === 0 && pendingFiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('instruments.lotsForm.attachments.empty')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {visibleAttachments.map((row) => {
            const downloadControlsDisabled = downloadingAttachmentIds.includes(row.id)
            const displayFilename = resolveAttachmentFilename(row.attachmentName)

            return (
              <li key={row.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {fileExtensionBadge(displayFilename)}
                </span>

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="block max-w-full truncate text-left text-sm font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
                    disabled={downloadControlsDisabled}
                    aria-busy={downloadControlsDisabled || undefined}
                    aria-label={`${t('instruments.lotsForm.attachments.openFileSr')}: ${displayFilename}`}
                    onClick={() => void handleDownload(row)}
                  >
                    {displayFilename}
                  </button>
                  {downloadControlsDisabled && isTrustedDownloadHref(row.downloadHref ?? '') && (
                    <p className="text-xs text-muted-foreground" aria-live="polite">
                      {t('instruments.lotsForm.attachments.downloading')}
                    </p>
                  )}
                  {row.attachmentDescription && (
                    <p className="truncate text-xs text-muted-foreground">
                      {row.attachmentDescription}
                    </p>
                  )}
                </div>

                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatDateTime(row.createdAt, row.timeZoneName)}
                </span>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={downloadControlsDisabled}
                    aria-busy={downloadControlsDisabled || undefined}
                    onClick={() => void handleDownload(row)}
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="sr-only">
                      {`${t('instruments.lotsForm.attachments.downloadSr')}: ${displayFilename}`}
                    </span>
                  </Button>

                  {permittedToDeleteAttachmentDocuments && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      disabled={downloadControlsDisabled}
                      onClick={() => onDeleteExisting?.(row.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">
                        {`${t('instruments.lotsForm.attachments.deleteSr')}: ${displayFilename}`}
                      </span>
                    </Button>
                  )}
                </div>
              </li>
            )
          })}

          {pendingFiles.map((pending) => (
            <li
              key={pending.id}
              className="flex flex-wrap items-center gap-3 bg-muted/30 px-3 py-2.5"
            >
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                {fileExtensionBadge(pending.file.name)}
              </span>

              <span className="min-w-0 shrink-0 truncate text-sm font-medium max-w-[180px]">
                {pending.file.name}
              </span>

              <div className="min-w-0 flex-1">
                <Input
                  value={pending.description}
                  onChange={(event) => updatePendingDescription(pending.id, event.target.value)}
                  placeholder={t('instruments.lotsForm.attachments.descriptionPlaceholder')}
                  className="h-8 text-sm"
                />
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => removePending(pending.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span className="sr-only">
                  {`${t('instruments.lotsForm.attachments.deleteSr')}: ${pending.file.name}`}
                </span>
              </Button>
            </li>
          ))}
        </ul>
      )}

      {permittedToCreateAttachmentDocuments && (
        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('instruments.lotsForm.attachments.addFile')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      )}
    </section>
  )
}
